// chessAI.js
// Lightweight browser AI with iterative deepening, alpha-beta pruning,
// move ordering, positional evaluation, and a transposition cache.

import {
  allLegalMoves,
  applyMoveToBoard,
  deriveNextState,
  isInCheck,
} from "./chessEngine";

const PIECE_VALUES = {
  P: 100,
  N: 320,
  B: 330,
  R: 500,
  Q: 900,
  K: 20000,
};

const CENTER_BONUS = [
  [0, 0, 2, 3, 3, 2, 0, 0],
  [0, 3, 5, 6, 6, 5, 3, 0],
  [2, 5, 8, 10, 10, 8, 5, 2],
  [3, 6, 10, 14, 14, 10, 6, 3],
  [3, 6, 10, 14, 14, 10, 6, 3],
  [2, 5, 8, 10, 10, 8, 5, 2],
  [0, 3, 5, 6, 6, 5, 3, 0],
  [0, 0, 2, 3, 3, 2, 0, 0],
];

export const AI_LEVELS = {
  beginner: {
    label: "Beginner",
    description: "Mostly legal random play with a little common sense.",
    timeMs: 80,
    maxDepth: 1,
    randomness: 0.55,
  },
  easy: {
    label: "Easy",
    description: "Looks ahead a little and protects obvious material.",
    timeMs: 180,
    maxDepth: 2,
    randomness: 0.28,
  },
  medium: {
    label: "Medium",
    description: "Alpha-beta search with positional evaluation.",
    timeMs: 450,
    maxDepth: 3,
    randomness: 0.08,
  },
  hard: {
    label: "Hard",
    description: "Deeper search, stronger move ordering, fewer mistakes.",
    timeMs: 900,
    maxDepth: 4,
    randomness: 0.02,
  },
  expert: {
    label: "Expert",
    description: "Strongest built-in browser search; may think for a moment.",
    timeMs: 1600,
    maxDepth: 5,
    randomness: 0,
  },
};

function boardHash(board, state, depth) {
  return (
    board
      .flat()
      .map((piece) => piece || "--")
      .join("") +
    "|" +
    state.turn +
    "|" +
    depth
  );
}

function evaluateBoard(
  board,
  state,
  perspective
) {
  let score = 0;
  let whiteBishops = 0;
  let blackBishops = 0;

  for (let r = 0; r < 8; r += 1) {
    for (let c = 0; c < 8; c += 1) {
      const piece = board[r][c];

      if (!piece) {
        continue;
      }

      const color = piece[0];
      const type = piece[1];

      let value =
        PIECE_VALUES[type] || 0;

      if (type === "P") {
        const advance =
          color === "w"
            ? 6 - r
            : r - 1;

        value += advance * 8;
        value += CENTER_BONUS[r][c];
      }

      if (type === "N") {
        value += CENTER_BONUS[r][c] * 2;
      }

      if (type === "B") {
        value += CENTER_BONUS[r][c];
        if (color === "w") {
          whiteBishops += 1;
        } else {
          blackBishops += 1;
        }
      }

      if (type === "R") {
        value += Math.round(
          CENTER_BONUS[r][c] * 0.35
        );
      }

      if (type === "Q") {
        value += Math.round(
          CENTER_BONUS[r][c] * 0.4
        );
      }

      if (type === "K") {
        const earlyGame =
          board
            .flat()
            .filter(
              (candidate) =>
                candidate &&
                candidate[1] !== "P" &&
                candidate[1] !== "K"
            ).length > 8;

        if (earlyGame) {
          const homeRow =
            color === "w" ? 7 : 0;

          if (
            r === homeRow &&
            [2, 6].includes(c)
          ) {
            value += 35;
          }
        } else {
          value += CENTER_BONUS[r][c];
        }
      }

      score +=
        color === perspective
          ? value
          : -value;
    }
  }

  if (whiteBishops >= 2) {
    score +=
      perspective === "w"
        ? 30
        : -30;
  }

  if (blackBishops >= 2) {
    score +=
      perspective === "b"
        ? 30
        : -30;
  }

  const ownState = {
    ...state,
    turn: perspective,
  };

  const oppState = {
    ...state,
    turn:
      perspective === "w"
        ? "b"
        : "w",
  };

  const mobility =
    allLegalMoves(
      board,
      ownState
    ).length -
    allLegalMoves(
      board,
      oppState
    ).length;

  score += mobility * 3;

  if (
    isInCheck(
      board,
      perspective
    )
  ) {
    score -= 45;
  }

  if (
    isInCheck(
      board,
      perspective === "w"
        ? "b"
        : "w"
    )
  ) {
    score += 35;
  }

  return score;
}

function movePriority(
  board,
  candidate
) {
  const piece =
    board[candidate.from.r][
      candidate.from.c
    ];

  const target =
    board[candidate.move.r][
      candidate.move.c
    ];

  let score = 0;

  if (candidate.move.promotion) {
    score += 10000;
  }

  if (target) {
    score +=
      5000 +
      (PIECE_VALUES[target[1]] || 0) -
      Math.round(
        (PIECE_VALUES[piece[1]] || 0) *
          0.08
      );
  }

  if (candidate.move.enPassant) {
    score += 5100;
  }

  if (candidate.move.castle) {
    score += 700;
  }

  const center =
    CENTER_BONUS[
      candidate.move.r
    ][candidate.move.c];

  score += center * 4;

  return score;
}

function orderedMoves(
  board,
  state
) {
  return allLegalMoves(
    board,
    state
  ).sort(
    (a, b) =>
      movePriority(board, b) -
      movePriority(board, a)
  );
}

function nextPosition(
  board,
  state,
  candidate
) {
  const nextBoard =
    applyMoveToBoard(
      board,
      candidate.from,
      candidate.move,
      "Q"
    );

  const nextState =
    deriveNextState(
      board,
      state,
      candidate.from,
      candidate.move
    );

  return {
    board: nextBoard,
    state: nextState,
  };
}

function terminalScore(
  board,
  state,
  perspective,
  ply
) {
  const legal =
    allLegalMoves(
      board,
      state
    );

  if (legal.length > 0) {
    return null;
  }

  if (
    isInCheck(
      board,
      state.turn
    )
  ) {
    return state.turn === perspective
      ? -999999 + ply
      : 999999 - ply;
  }

  return 0;
}

function alphaBeta({
  board,
  state,
  depth,
  alpha,
  beta,
  perspective,
  deadline,
  cache,
  nodes,
  ply,
}) {
  nodes.count += 1;

  if (
    performance.now() >= deadline
  ) {
    nodes.timedOut = true;
    return evaluateBoard(
      board,
      state,
      perspective
    );
  }

  const terminal = terminalScore(
    board,
    state,
    perspective,
    ply
  );

  if (terminal !== null) {
    return terminal;
  }

  if (depth <= 0) {
    return evaluateBoard(
      board,
      state,
      perspective
    );
  }

  const cacheKey =
    boardHash(
      board,
      state,
      depth
    );

  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  const moves =
    orderedMoves(
      board,
      state
    );

  const maximizing =
    state.turn === perspective;

  let best = maximizing
    ? -Infinity
    : Infinity;

  for (const candidate of moves) {
    const next =
      nextPosition(
        board,
        state,
        candidate
      );

    const score =
      alphaBeta({
        board: next.board,
        state: next.state,
        depth: depth - 1,
        alpha,
        beta,
        perspective,
        deadline,
        cache,
        nodes,
        ply: ply + 1,
      });

    if (maximizing) {
      best = Math.max(
        best,
        score
      );

      alpha = Math.max(
        alpha,
        score
      );
    } else {
      best = Math.min(
        best,
        score
      );

      beta = Math.min(
        beta,
        score
      );
    }

    if (
      beta <= alpha ||
      nodes.timedOut
    ) {
      break;
    }
  }

  if (!nodes.timedOut) {
    cache.set(
      cacheKey,
      best
    );
  }

  return best;
}

function scoreRootMoves(
  board,
  state,
  perspective,
  depth,
  deadline
) {
  const moves =
    orderedMoves(
      board,
      state
    );

  const cache =
    new Map();

  const nodes = {
    count: 0,
    timedOut: false,
  };

  const scored = [];

  for (const candidate of moves) {
    const next =
      nextPosition(
        board,
        state,
        candidate
      );

    const score =
      alphaBeta({
        board: next.board,
        state: next.state,
        depth: depth - 1,
        alpha: -Infinity,
        beta: Infinity,
        perspective,
        deadline,
        cache,
        nodes,
        ply: 1,
      });

    scored.push({
      candidate,
      score,
    });

    if (nodes.timedOut) {
      break;
    }
  }

  scored.sort(
    (a, b) =>
      b.score - a.score
  );

  return {
    scored,
    nodes,
  };
}

function chooseWithRandomness(
  scored,
  randomness
) {
  if (!scored.length) {
    return null;
  }

  if (
    randomness <= 0 ||
    scored.length === 1
  ) {
    return scored[0].candidate;
  }

  const shortlist =
    scored.slice(
      0,
      Math.min(
        scored.length,
        randomness > 0.4
          ? 8
          : randomness > 0.15
          ? 4
          : 2
      )
    );

  if (
    Math.random() >
    randomness
  ) {
    return shortlist[0].candidate;
  }

  return shortlist[
    Math.floor(
      Math.random() *
        shortlist.length
    )
  ].candidate;
}

export function chooseAiMove(
  board,
  state,
  level = "medium"
) {
  const config =
    AI_LEVELS[level] ||
    AI_LEVELS.medium;

  const legal =
    orderedMoves(
      board,
      state
    );

  if (!legal.length) {
    return {
      move: null,
      depth: 0,
      nodes: 0,
      elapsedMs: 0,
    };
  }

  if (level === "beginner") {
    const candidate =
      legal[
        Math.floor(
          Math.random() *
            legal.length
        )
      ];

    return {
      move: candidate,
      depth: 0,
      nodes: legal.length,
      elapsedMs: 0,
    };
  }

  const started =
    performance.now();

  const deadline =
    started +
    config.timeMs;

  let completedScored = legal.map(
    (candidate) => ({
      candidate,
      score: movePriority(
        board,
        candidate
      ),
    })
  );

  let completedDepth = 0;
  let totalNodes = 0;

  for (
    let depth = 1;
    depth <= config.maxDepth;
    depth += 1
  ) {
    const result =
      scoreRootMoves(
        board,
        state,
        state.turn,
        depth,
        deadline
      );

    totalNodes +=
      result.nodes.count;

    if (
      result.nodes.timedOut ||
      !result.scored.length
    ) {
      break;
    }

    completedScored =
      result.scored;

    completedDepth = depth;

    if (
      performance.now() >=
      deadline
    ) {
      break;
    }
  }

  return {
    move: chooseWithRandomness(
      completedScored,
      config.randomness
    ),
    depth: completedDepth,
    nodes: totalNodes,
    elapsedMs: Math.round(
      performance.now() -
        started
    ),
  };
}
